// @ts-check
/**
 * Typed client for the care-management provider-messaging (Updox/Direct secure
 * messaging) API.
 *
 * This is the first "typed API client" in the codebase. The pattern: one module
 * per backend domain under `src/services/api/`, with thin async functions per
 * operation that return `response.data` already typed.
 *
 * TYPES ARE GENERATED, NOT HAND-WRITTEN. The shapes below are aliases into
 * `schema.d.ts`, which is generated from the backend OpenAPI schema by:
 *
 *     npm run generate:types-from-openapi-schema
 *
 * Re-run that whenever the backend schema changes; `// @ts-check` will then flag
 * any code in this file that no longer matches. `schema.d.ts` is a type-only
 * declaration file — it has no runtime code and never enters the build. The
 * `import('./schema')` references live inside JSDoc comments, so they are
 * invisible at runtime; only the editor's TypeScript language service reads them.
 *
 * Uses the global axios instance, so the config set up in main.js applies:
 * `baseURL` (VITE_API_BASE_URL), `withCredentials`, the request interceptor that
 * injects `tenant_slug`, and the 401 -> /login handler. Paths are kept relative.
 */
import axios from 'axios'

const BASE = '/api/care-management/provider-messaging'

/** @typedef {import('./schema').components['schemas']} Schemas */
/** @typedef {import('./schema').components['schemas']['FolderEnum']} Folder */
/** @typedef {Schemas['ProviderMessagingUser']} ProviderMessagingUser */
/** @typedef {Schemas['ProviderMessagingMessageSummary']} ProviderMessagingMessageSummary */
/** @typedef {Schemas['ProviderMessagingMessage']} ProviderMessagingMessage */
/** @typedef {Schemas['ProviderMessagingError']} ProviderMessagingError */
/** @typedef {Schemas['ProviderMessagingMarkMessage']} ProviderMessagingMarkMessage */
/** @typedef {Schemas['ProviderMessagingMoveMessage']} ProviderMessagingMoveMessage */
/** @typedef {Schemas['ProviderMessagingSendMessage']} ProviderMessagingSendMessage */
/** @typedef {Schemas['ProviderSummaryTextResponse']} ProviderSummaryTextResponse */
/** @typedef {{ name: string, fileName: string, mimeType: string, content: string }} FileAttachment */
/** @typedef {Schemas['HispMailbox']} HispMailbox */
/** @typedef {Schemas['HispMailboxStatusEnum']} HispMailboxStatus */
/** @typedef {Schemas['ProviderMessage']} ProviderMessage */
/** @typedef {Schemas['ProviderMessagingUnreadCount']} ProviderMessagingUnreadCount */

/**
 * Run a provider-messaging request. HTTP response status identifies the failure
 * category; a 400 response may include a ProviderMessagingError with a
 * human-readable message. The error is re-thrown so callers can react.
 * @template T
 * @param {Promise<T>} promise
 * @returns {Promise<T>}
 */
async function call(promise) {
  try {
    return await promise
  } catch (e) {
    const err = /** @type {{ response?: { status?: number } }} */ (e)
    if (err && err.response && err.response.status === 400) {
      console.error('[provider-messaging] request failed (400):', errorMessage(e))
    }
    throw e
  }
}

/**
 * GET /folders/ — available folder metadata (key + label). Icons are a frontend concern.
 * @returns {Promise<{key: string, label: string}[]>}
 */
export async function getFolders() {
  const { data } = await axios.get(`${BASE}/folders/`)
  return data
}

/**
 * GET /messages/ — message summaries for a folder.
 * @param {{ folder?: string }} [params]
 * @returns {Promise<ProviderMessagingMessageSummary[]>}
 */
export async function listMessages(params) {
  const { data } = await call(axios.get(`${BASE}/messages/`, { params }))
  return data
}

/**
 * GET /messages/{message_id}/ — full message detail.
 * @param {string | number} messageId
 * @returns {Promise<ProviderMessagingMessage>}
 */
export async function getMessage(messageId) {
  const { data } = await call(axios.get(`${BASE}/messages/${messageId}/`))
  return data
}

/**
 * POST /messages/{message_id}/mark/ — set read/unread. 200 has no body.
 * @param {string | number} messageId
 * @param {ProviderMessagingMarkMessage} body
 * @returns {Promise<void>}
 */
export async function markMessage(messageId, body) {
  await call(axios.post(`${BASE}/messages/${messageId}/mark/`, body))
}

/**
 * POST /messages/{message_id}/move/ — move to another folder. 200 has no body.
 * @param {string | number} messageId
 * @param {ProviderMessagingMoveMessage} body
 * @returns {Promise<void>}
 */
export async function moveMessage(messageId, body) {
  await call(axios.post(`${BASE}/messages/${messageId}/move/`, body))
}

/**
 * POST /send/ — send a message via Updox. Persists an outbound ProviderMessage
 * record in the database automatically. Returns the Updox message_id.
 * @param {ProviderMessagingSendMessage & { entity_id: number | null, attachments?: FileAttachment[] }} body
 * @returns {Promise<{ message_id: string }>}
 */
export async function sendMessage(body) {
  const { data } = await call(axios.post(`${BASE}/send/`, body))
  return data
}

/**
 * POST /send-engagement-summary/ -- send selected engagement summary documents
 * as a C-CDA Progress Note in an XDM package via Direct secure messaging.
 * @param {{ recipientDirectAddresses: string[], workIds: number[], entityId: number, subject?: string }} params
 * @returns {Promise<{ message_id: number, external_id: string, documents_sent: string[], recipients: string[] }>}
 */
export async function sendEngagementSummary({ recipientDirectAddresses, workIds, entityId, subject = '' }) {
  const { data } = await call(axios.post(`${BASE}/send-engagement-summary/`, {
    recipient_direct_addresses: recipientDirectAddresses,
    work_ids: workIds,
    entity_id: entityId,
    subject,
  }))
  return data
}

/**
 * POST /preview-engagement-summary/ -- generate C-CDA XML for preview.
 * Returns raw XML bytes (not JSON).
 * @param {{ workIds: number[], entityId: number }} params
 * @returns {Promise<string>} C-CDA XML string
 */
export async function previewEngagementSummary({ workIds, entityId }) {
  const { data } = await call(axios.post(`${BASE}/preview-engagement-summary/`, {
    work_ids: workIds,
    entity_id: entityId,
  }))
  return data.xml
}

/**
 * GET /summary-text/{workId}/ — fetch a Provider Summary for the editable
 * plaintext Direct-message body and suggested subject.
 * @param {number | string} workId
 * @returns {Promise<ProviderSummaryTextResponse>}
 */
export async function getProviderSummaryText(workId) {
  const { data } = await call(axios.get(`${BASE}/summary-text/${workId}/`))
  return data
}

/**
 * POST /messages/{message_id}/link-entity/ — link an entity (patient) to a specific
 * Updox message. Creates a ProviderMessage record if none exists yet.
 * @param {string | number} messageId
 * @param {{ entity_id: number }} payload
 * @returns {Promise<{ entity_id: number, entity_name: string }>}
 */
export async function linkMessageEntity(messageId, payload) {
  const { data } = await call(axios.post(`${BASE}/messages/${messageId}/link-entity/`, payload))
  return data
}

// ─── Entity messages ─────────────────────────────────────────────────────────

/**
 * GET /entity/{entityId}/messages/ — all ProviderMessages linked to an entity
 * (e.g. a patient), ordered most-recent first. Thread grouping (by normalized
 * subject) is done client-side; see groupByThread() in Provider_Messages.vue.
 * @param {number | string} entityId
 * @returns {Promise<ProviderMessage[]>}
 */
export async function listEntityMessages(entityId) {
  const { data } = await call(axios.get(`${BASE}/entity/${entityId}/messages/`))
  return data
}

// ─── Mailbox ─────────────────────────────────────────────────────────────────

/**
 * GET /mailbox/ — retrieve the current user's HispMailbox.
 * Throws (404) if no mailbox has been provisioned yet.
 * @returns {Promise<HispMailbox>}
 */
export async function getMailbox() {
  const { data } = await call(axios.get(`${BASE}/mailbox/`))
  return data
}


// ─── Contacts ─────────────────────────────────────────────────────────────────

/** @typedef {Schemas['ProviderMessagingAddressBookEntry']} ProviderMessagingAddressBookEntry */

/**
 * GET /contacts/ — unified address book: internal Kairon staff + saved external contacts.
 * Each entry includes a `source` field: "internal" | "contact". The caller's own mailbox
 * is excluded. `search` is currently applied client-side by callers, not server-side.
 * @param {string} [search] Optional filter applied server-side across name / org / address.
 * @returns {Promise<ProviderMessagingAddressBookEntry[]>}
 */
export async function listContacts(search) {
  const params = search ? { search } : {}
  const { data } = await call(axios.get(`${BASE}/contacts/`, { params }))
  return data
}

/**
 * POST /contacts/ — upsert a contact (keyed by direct_address). Contacts are
 * append-only; there is no update or delete endpoint.
 * @param {{ direct_address: string, first_name?: string, last_name?: string, organization?: string, npi?: string, phone?: string, fax?: string, address1?: string, address2?: string, city?: string, state?: string, postal?: string }} payload
 * @returns {Promise<ProviderMessageContact>}
 */
export async function createContact(payload) {
  const { data } = await call(axios.post(`${BASE}/contacts/`, payload))
  return data
}

// ─── Provider directory search ────────────────────────────────────────────────

/** @typedef {Schemas['ProviderDirectoryResult']} ProviderDirectoryResult */
/** @typedef {Schemas['ProviderDirectorySearchResults']} ProviderDirectorySearchResults */

/**
 * GET /contacts/search/ — search the Updox provider directory. Field-qualified only:
 * at least one of firstName/lastName/organization/city/state/postal/npi is required.
 * Values may include `*` wildcards (e.g. "Sm*"). `state` must be a 2-letter USPS
 * abbreviation (see @/constants/usStates). Results are paginated 25 per page (fixed
 * server-side); pass `page` (1-indexed) to fetch a later page of the same query.
 * @param {{ firstName?: string, lastName?: string, organization?: string, city?: string, state?: string, postal?: string, npi?: string, page?: number }} [params]
 * @returns {Promise<ProviderDirectorySearchResults>}
 */
export async function searchDirectory({ firstName, lastName, organization, city, state, postal, npi, page } = {}) {
  const params = Object.fromEntries(
    Object.entries({
      first_name: firstName,
      last_name: lastName,
      organization,
      city,
      state,
      postal,
      npi,
      page,
    }).filter(([, v]) => v !== undefined && v !== ''),
  )
  const { data } = await call(axios.get(`${BASE}/contacts/search/`, { params }))
  return data
}

export async function waterfallSearch({ npi, firstName, lastName, organization, practiceState, practiceEntityId, patientEntityId } = {}) {
  const params = Object.fromEntries(
    Object.entries({
      npi,
      first_name: firstName,
      last_name: lastName,
      organization,
      practice_state: practiceState,
      practice_entity_id: practiceEntityId,
      patient_entity_id: patientEntityId,
    }).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  )
  const { data } = await call(axios.get(`${BASE}/contacts/waterfall-search/`, { params }))
  return data
}

// ─── Unread count ────────────────────────────────────────────────────────────

/**
 * GET /unread-count/ — number of unread messages for the current user.
 * @returns {Promise<ProviderMessagingUnreadCount>}
 */
export async function getUnreadCount() {
  const { data } = await call(axios.get(`${BASE}/unread-count/`))
  return data
}

/**
 * POST /provision-mailbox/ — provision a HISP mailbox for a user (admin only).
 * @param {number} userId
 * @returns {Promise<import('./schema').components['schemas']['HispMailbox']>}
 */
export async function provisionMailbox(userId) {
  const { data } = await call(axios.post(`${BASE}/provision-mailbox/`, { user_id: userId }))
  return data
}

/**
 * POST /deactivate-mailbox/ — deactivate a user's HISP mailbox (admin only).
 * @param {number} userId
 * @returns {Promise<import('./schema').components['schemas']['HispMailbox']>}
 */
export async function deactivateMailbox(userId) {
  const { data } = await call(axios.post(`${BASE}/deactivate-mailbox/`, { user_id: userId }))
  return data
}

/**
 * POST /reactivate-mailbox/ — reactivate an inactive HISP mailbox (admin only).
 * @param {number} userId
 * @returns {Promise<import('./schema').components['schemas']['HispMailbox']>}
 */
export async function reactivateMailbox(userId) {
  const { data } = await call(axios.post(`${BASE}/reactivate-mailbox/`, { user_id: userId }))
  return data
}

// ─── Delivery report ─────────────────────────────────────────────────────────

/** @typedef {Schemas['DeliveryReport']} DeliveryReport */
/** @typedef {Schemas['DeliveryReportSend']} DeliveryReportSend */
/** @typedef {Schemas['DeliveryReportFailNotification']} DeliveryReportFailNotification */
/** @typedef {Schemas['DeliveryReportEhrSummary']} DeliveryReportEhrSummary */
/** @typedef {Schemas['DeliveryReportUser']} DeliveryReportUser */

/**
 * GET /message-delivery-report/ — cached delivery report.
 * Returns { generated_at: null, status: 'idle' } if no report exists yet.
 * @returns {Promise<DeliveryReport & { status?: string }>}
 */
export async function getDeliveryReport() {
  const { data } = await call(axios.get(`${BASE}/message-delivery-report/`))
  return data
}

/**
 * POST /message-delivery-report/refresh/ — kick off background report generation.
 * Returns 202 immediately; poll GET until status changes from 'generating'.
 * @returns {Promise<{ status: string }>}
 */
export async function refreshDeliveryReport() {
  const { data } = await call(axios.post(`${BASE}/message-delivery-report/refresh/`))
  return data
}

/**
 * GET /message-delivery-report/notes/ — all notes for the current delivery report.
 * @returns {Promise<Array<{ id: number, note_key: string, note_type: string, text: string, created_by_name: string, updated_by_name: string, created_at: string, updated_at: string }>>}
 */
export async function getDeliveryReportNotes() {
  const { data } = await call(axios.get(`${BASE}/message-delivery-report/notes/`))
  return data
}

/**
 * PUT /message-delivery-report/notes/save — upsert a delivery report note.
 * @param {string} noteKey
 * @param {string} noteType
 * @param {string} text
 * @returns {Promise<{ id: number, note_key: string, note_type: string, text: string, created_by_name: string, updated_by_name: string, created_at: string, updated_at: string }>}
 */
export async function saveDeliveryReportNote(noteKey, noteType, text) {
  const { data } = await call(axios.put(`${BASE}/message-delivery-report/notes/save/`, {
    note_key: noteKey,
    note_type: noteType,
    text,
  }))
  return data
}

/**
 * GET /message-delivery-report/available-lbns — tenant's practice LBNs for dropdown.
 */
export async function getAvailableLbns() {
  const { data } = await call(axios.get(`${BASE}/message-delivery-report/available-lbns/`))
  return data.lbns
}

/**
 * PUT /message-delivery-report/update-lbn — set LBN on a contact by direct address.
 */
export async function updateContactLbn(directAddress, lbn) {
  const { data } = await call(axios.put(`${BASE}/message-delivery-report/update-lbn/`, {
    direct_address: directAddress,
    lbn,
  }))
  return data
}

// ─── Error helpers ────────────────────────────────────────────────────────────

/**
 * Extract a human-readable message from a failed request. Provider-messaging
 * error responses carry an `error` message; fall back to the axios error's own
 * message otherwise.
 * @param {unknown} e
 * @returns {string}
 */
export function errorMessage(e) {
  const err = /** @type {{ response?: { data?: ProviderMessagingError }, message?: string }} */ (e)
  const data = err && err.response && err.response.data
  if (data && typeof data.error === 'string') return data.error
  return (err && err.message) || 'Request failed.'
}
